/**
 * EndcardOverlay — visible while phase === "endcard".
 *
 * Per Figma layer note: "On end: video ends, rectangle unit fades in, holds
 * duration, non-dismissable, hover state will expand/brighten. On click goes
 * to URL. Upper right has 5 second countdown until next clip auto plays."
 *
 * Layout:
 *   ┌──────────────────────────────────────────────────────┐
 *   │  [next-clip title]                  [○ countdown]    │  ← player frame top strip
 *   │                                                      │
 *   │            ┌──────────────────────────┐              │
 *   │            │  <img src=assetUrl />    │              │
 *   │            │  optional title strip    │              │
 *   │            └──────────────────────────┘              │
 *   └──────────────────────────────────────────────────────┘
 *
 * V2 model: the campaign provides a complete creative as `assetUrl` (image
 * or animated GIF) — same shape IAB ads ship in. The endcard frame is just
 * a rounded container with click-through; the bespoke rating-dial layout
 * was Leetify-specific and is gone.
 *
 * Auto-advance is owned by the ad-slot phase machine in ../ViewModel.tsx;
 * here we just render the creative + countdown.
 */
import { useEffect, useState } from "react";
import { useRecoilValue } from "recoil";
import { Box, Flex, Image, Text } from "rebass/styled-components";
import styled from "styled-components";

import {
  BrandedActiveClipIndexState,
  BrandedFullscreenState,
  BrandedLayoutVariantState,
  BrandedPlaybackPhaseState,
  BrandedPlaylistClipsState,
  BrandedSponsorConfigState,
  BrandedViewerGeoState,
} from "../../State";
import { composeBrandedTitle } from "../../titleComposer";
import { fireAdClickBeacon, useImpressionBeacon } from "../../tracking";

const Veil = styled(Box)<{ $visible: boolean }>`
  position: absolute;
  inset: 0;
  z-index: 5;
  background: rgba(0, 0, 0, 0.62);
  pointer-events: ${({ $visible }) => ($visible ? "auto" : "none")};
  opacity: ${({ $visible }) => ($visible ? 1 : 0)};
  transition: opacity 400ms ease;
`;

/* Top strip — next clip title on the left, countdown on the right. Sits
   inside the player frame bounds, not the card. */
const TopStrip = styled(Flex)`
  align-items: center;
  /* Only the countdown ring lives here now — anchor it to the right
     edge of the player frame. */
  justify-content: flex-end;
  gap: 12px;
  left: 16px;
  position: absolute;
  right: 16px;
  top: 12px;
`;

const UpNextLabel = styled(Text)`
  color: rgba(255, 255, 255, 0.6);
  font-size: 10px;
  font-weight: 800;
  letter-spacing: 0.6px;
  margin-right: 6px;
  text-transform: uppercase;
`;

const NextClipTitle = styled(Text)`
  color: #fff;
  /* Smaller on desktop than the previous 14px — the strip sits inside
     the narrow HLTV iframe and the title was visually competing with
     the player chrome. */
  font-size: 10px;
  font-weight: 700;
  letter-spacing: 0.2px;
  overflow: hidden;
  text-overflow: ellipsis;
  text-shadow: 0 1px 4px rgba(0, 0, 0, 0.6);
  white-space: nowrap;
`;

/* Circular countdown — SVG ring sweeps from full to empty as the
   autoAdvance window elapses. The static <circle> is the track; the
   animated <circle> uses stroke-dasharray to draw the remaining arc. */
const CountdownWrap = styled(Flex)<{ $fs?: boolean; $bp?: boolean }>`
  align-items: center;
  background: rgba(0, 0, 0, 0.7);
  border-radius: 50%;
  flex: 0 0 auto;
  /* 26px in-iframe; bottom-playlist embed gets a touch larger since
     the bigger card scale makes the 26px ring read undersized; scales
     up dramatically when the player is in fullscreen so the countdown
     reads at TV viewing distance. */
  height: ${({ $fs, $bp }) => ($fs ? "72px" : $bp ? "36px" : "26px")};
  justify-content: center;
  position: relative;
  width: ${({ $fs, $bp }) => ($fs ? "72px" : $bp ? "36px" : "26px")};
`;

const CountdownNumber = styled(Text)<{ $fs?: boolean; $bp?: boolean }>`
  color: #fff;
  font-size: ${({ $fs, $bp }) => ($fs ? "26px" : $bp ? "13px" : "9px")};
  font-weight: 800;
  position: relative;
  z-index: 2;
`;

const CountdownRing = styled.svg<{ $fs?: boolean; $bp?: boolean }>`
  height: ${({ $fs, $bp }) => ($fs ? "72px" : $bp ? "36px" : "26px")};
  left: 0;
  position: absolute;
  top: 0;
  transform: rotate(-90deg);
  width: ${({ $fs, $bp }) => ($fs ? "72px" : $bp ? "36px" : "26px")};
`;

/* The card itself: rounded frame with the campaign creative inside. The
   creative image fills the frame; the optional title sits as a thin
   header strip above the image. */
const Card = styled.a<{ $expandOnHover: boolean }>`
  background: rgba(12, 19, 24, 0.8);
  border: 1px solid rgba(255, 255, 255, 0.08);
  border-radius: 20px;
  box-shadow: 0 18px 42px rgba(0, 0, 0, 0.55);
  color: #fff;
  cursor: pointer;
  display: flex;
  flex-direction: column;
  left: 50%;
  max-height: 70%;
  max-width: 60%;
  overflow: hidden;
  position: absolute;
  text-decoration: none;
  top: 50%;
  transform: translate(-50%, -50%);
  transition:
    transform 180ms ease,
    filter 180ms ease,
    box-shadow 180ms ease;
  width: 60%;

  ${({ $expandOnHover }) =>
    $expandOnHover &&
    `&:hover {
      transform: translate(-50%, -50%) scale(1.02);
      filter: brightness(1.08);
      box-shadow: 0 22px 50px rgba(0, 0, 0, 0.6);
    }`}
`;

const CardHeader = styled(Flex)<{ $fs?: boolean; $bp?: boolean }>`
  align-items: center;
  justify-content: space-between;
  padding: ${({ $fs, $bp }) =>
    $fs ? "22px 28px" : $bp ? "16px 18px" : "12px 14px"};

  & svg {
    width: ${({ $fs, $bp }) =>
      $fs ? "26px" : $bp ? "15px" : "11px"} !important;
    height: ${({ $fs, $bp }) =>
      $fs ? "26px" : $bp ? "15px" : "11px"} !important;
  }
`;

const CardTitle = styled(Text)<{ $fs?: boolean; $bp?: boolean }>`
  color: #f7fcfd;
  /* In-iframe: small label above the creative. Bottom-playlist:
     bumped to read at the bigger embed scale. Fullscreen: scales up
     dramatically to match the full-viewport canvas. */
  font-size: ${({ $fs, $bp }) => ($fs ? "28px" : $bp ? "15px" : "10px")};
  font-weight: 800;
  letter-spacing: ${({ $fs, $bp }) =>
    $fs ? "0.4px" : $bp ? "0.2px" : "0.1px"};
  text-shadow: 0 2px 6px rgba(0, 0, 0, 0.4);
`;

const CreativeImg = styled(Image)`
  display: block;
  flex: 1 1 auto;
  height: 100%;
  min-height: 0;
  object-fit: cover;
  width: 100%;
`;

/* Lightweight fallback for inline:* sentinels or empty assetUrl — the
   real V2 path is a real image URL so the campaign owns the visual
   entirely. We keep this so the demo still renders something when the
   sentinel is in play. */
const CreativeFallback = styled(Flex)`
  align-items: center;
  background: linear-gradient(
    135deg,
    rgba(255, 255, 255, 0.04) 0%,
    rgba(255, 255, 255, 0.02) 100%
  );
  flex: 1 1 auto;
  flex-direction: column;
  gap: 6px;
  justify-content: center;
  min-height: 180px;
  padding: 24px;
`;

const FallbackLabel = styled(Text)`
  color: rgba(255, 255, 255, 0.4);
  font-size: 10px;
  font-weight: 700;
  letter-spacing: 1.2px;
  text-transform: uppercase;
`;

/* External-link glyph — tiny inline SVG, no icon-lib dep. */
const LaunchIcon = ({ size = 16 }: { size?: number }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 16 16"
    fill="none"
    stroke="currentColor"
    strokeWidth={1.6}
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M9 2h5v5" />
    <path d="M14 2 8 8" />
    <path d="M12 9v4a1 1 0 0 1-1 1H3a1 1 0 0 1-1-1V5a1 1 0 0 1 1-1h4" />
  </svg>
);

interface CountdownProps {
  /** Seconds remaining (visible). */
  secondsLeft: number;
  /** Total countdown window so the ring fraction is computed correctly. */
  totalSeconds: number;
  /** Mirror BrandedFullscreenState — scales the ring up at TV size. */
  isFullscreen: boolean;
  /** Bottom-playlist embed gets a mid-tier ring size. */
  isBottomPlaylist: boolean;
}

const CountdownRingDisplay = ({
  secondsLeft,
  totalSeconds,
  isFullscreen,
  isBottomPlaylist,
}: CountdownProps) => {
  const radius = 18;
  const circumference = 2 * Math.PI * radius;
  const fraction = Math.max(0, Math.min(1, secondsLeft / totalSeconds));
  const dashOffset = circumference * (1 - fraction);
  return (
    <CountdownWrap $fs={isFullscreen} $bp={isBottomPlaylist}>
      <CountdownRing
        $fs={isFullscreen}
        $bp={isBottomPlaylist}
        viewBox="0 0 44 44"
      >
        <circle
          cx="22"
          cy="22"
          r={radius}
          stroke="rgba(255, 255, 255, 0.18)"
          strokeWidth="2.5"
          fill="none"
        />
        <circle
          cx="22"
          cy="22"
          r={radius}
          stroke="var(--allstar-accent, #3C87D2)"
          strokeWidth="2.5"
          fill="none"
          strokeLinecap="round"
          strokeDasharray={circumference}
          strokeDashoffset={dashOffset}
          style={{ transition: "stroke-dashoffset 950ms linear" }}
        />
      </CountdownRing>
      <CountdownNumber $fs={isFullscreen} $bp={isBottomPlaylist}>
        {Math.max(0, secondsLeft)}
      </CountdownNumber>
    </CountdownWrap>
  );
};

export const EndcardOverlay = () => {
  const phase = useRecoilValue(BrandedPlaybackPhaseState);
  const sponsor = useRecoilValue(BrandedSponsorConfigState);
  const clips = useRecoilValue(BrandedPlaylistClipsState);
  const activeIndex = useRecoilValue(BrandedActiveClipIndexState);
  const isFullscreen = useRecoilValue(BrandedFullscreenState);
  const layoutVariant = useRecoilValue(BrandedLayoutVariantState);
  const isBottomPlaylist = layoutVariant === "bottomPlaylist";
  const viewerGeo = useRecoilValue(BrandedViewerGeoState);
  const endcard = sponsor?.slots.endcard;
  const visible = phase === "endcard" && !!endcard;

  // Endcard impression only counts during the endcard phase (the veil + card
  // are off-screen / pointer-events:none otherwise). Gating via `enabled`
  // keeps the IO callback from registering until the slot is actually live.
  const impressionRef = useImpressionBeacon({
    slot: "endcard",
    creativeId: endcard?.id,
    enabled: visible,
  });

  const handleEndcardClick = () => {
    if (!endcard) return;
    fireAdClickBeacon({
      campaignId: sponsor?.campaignId,
      slot: "endcard",
      creativeId: endcard.id,
      viewerGeo,
    });
  };

  // Local countdown for display; the phase machine in ViewModel actually
  // advances. This is a UI mirror only.
  const [secondsLeft, setSecondsLeft] = useState<number>(0);
  useEffect(() => {
    if (!visible || !endcard) {
      setSecondsLeft(0);
      return;
    }
    setSecondsLeft(Math.ceil(endcard.autoAdvanceMs / 1000));
    const t = window.setInterval(() => {
      setSecondsLeft((s) => (s > 0 ? s - 1 : 0));
    }, 1000);
    return () => window.clearInterval(t);
  }, [visible, endcard]);

  if (!endcard) return <Veil $visible={false} />;

  const totalSeconds = Math.ceil(endcard.autoAdvanceMs / 1000);
  const nextClip = clips?.[(activeIndex + 1) % (clips?.length || 1)] ?? null;
  const nextClipTitle = composeBrandedTitle(nextClip);

  // Real CDN URLs render directly; inline:* sentinels (used in V1 demo)
  // fall through to the placeholder.
  const hasRealCreative =
    !!endcard.assetUrl &&
    !endcard.assetUrl.startsWith("inline:") &&
    !endcard.assetUrl.startsWith("data:");

  return (
    <Veil $visible={visible}>
      {/* Top strip is now JUST the countdown ring — Up Next label and
          next-clip title were dropped as visual noise. The ring alone
          communicates "another clip is queued" without the extra copy. */}
      <TopStrip>
        <CountdownRingDisplay
          secondsLeft={secondsLeft}
          totalSeconds={totalSeconds}
          isFullscreen={isFullscreen}
          isBottomPlaylist={isBottomPlaylist}
        />
      </TopStrip>

      <Card
        href={endcard.ctaUrl}
        target="_blank"
        rel="noopener noreferrer"
        $expandOnHover={endcard.expandOnHover}
        ref={impressionRef}
        onClick={handleEndcardClick}
      >
        {endcard.title && (
          <CardHeader $fs={isFullscreen} $bp={isBottomPlaylist}>
            <CardTitle $fs={isFullscreen} $bp={isBottomPlaylist}>
              {endcard.title}
            </CardTitle>
            <Box sx={{ color: "#fff", display: "inline-flex" }}>
              <LaunchIcon size={isBottomPlaylist ? 14 : 11} />
            </Box>
          </CardHeader>
        )}
        {hasRealCreative ? (
          <CreativeImg src={endcard.assetUrl} alt={endcard.altText} />
        ) : (
          <CreativeFallback>
            <FallbackLabel>{sponsor?.brandName ?? "Sponsor"}</FallbackLabel>
            <Text sx={{ color: "rgba(255,255,255,0.55)", fontSize: 11 }}>
              Creative pending — set <code>endcard.assetUrl</code> to a real
              image URL.
            </Text>
          </CreativeFallback>
        )}
      </Card>
    </Veil>
  );
};
